<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import ListingPagination from '@/components/listing-pagination'
import i18n from '@/i18n'
import { mapState } from 'vuex'
import { SESSION, SHARED } from '@/constants'
import { sessionHelper, ddlHelper, tenantHelper } from '@/utils'
import { tenantService } from '@/helpers/services/tenant.service'
import { USERS_MODULE } from '@/state/modules/users'
import { ROLE_MODULE } from '@/state/modules/role'
import { usersMethods, roleMethods } from '@/state/helpers'

/**
 * User management.
 *
 * ── How this differs from the Team screen ───────────────────────────────
 * /team is the agent desk's own four-button page: a CS supervisor on shift
 * adding somebody, unlocking somebody, handing a password over. It needs no
 * mail server and it is gated on users.is_admin.
 *
 * This is the admin screen: paginated, filtered, role-aware, gated on spatie
 * permissions, and it never shows a password — the server mails a first-time
 * link. Both exist on purpose; see the backend's UserController.
 */
export default {
  page: { title: i18n.t('label.listingX', [i18n.t('module.user')]) },
  components: { Layout, PageHeader, MultiSelect, ListingPagination },
  data() {
    return {
      title: i18n.t('label.listingX', [i18n.t('module.user')]),
      breadcrumbItems: [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.user'), active: true },
      ],

      // Every filter is a multi-select, so each default is an EMPTY ARRAY —
      // which means "no constraint" and is why there is no "All" option.
      statusDdl: ddlHelper.getActivationStatusMulti(),
      lockDdl: ddlHelper.getAccountLockStatusMulti(),
      adminDdl: ddlHelper.getYesNoMulti(),
      tenantDdl: [],
      /** Brands as the server named them, for the scoped-list notice below. */
      tenantRows: [],

      filter: '',
      searchCriteria: {},
      showFilters: false,

      fields: [
        { key: 'name', sortable: true, label: i18n.t('label.name') },
        { key: 'email', sortable: true, label: i18n.t('label.email') },
        { key: 'tenant', sortable: false, label: i18n.t('label.brand') },
        { key: 'roles', sortable: false, label: i18n.t('label.role') },
        { key: 'isActive', sortable: false, label: i18n.t('label.status') },
        { key: 'isAllowLogin', sortable: false, label: i18n.t('user.allowLogin') },
        { key: 'isLocked', sortable: false, label: i18n.t('label.accountStatus') },
        { key: 'action', sortable: false, label: i18n.t('label.action') },
      ],
    }
  },
  computed: {
    ...mapState(USERS_MODULE, {
      tableRows: (state) => state.list.data,
      pagination: (state) => state.list.pagination,
      status: (state) => state.status,
    }),
    ...mapState(ROLE_MODULE, { roleRows: (state) => state.list.data }),
    roleDdl() {
      return this.roleRows.map((r) => ({ text: r.name, value: r.id }))
    },
    /** How many filters are narrowing the list, for the toggle's badge — the
     *  filter panel is collapsed by default and a hidden active filter is how
     *  "where did my rows go" happens. */
    activeFilterCount() {
      const c = this.searchCriteria
      return [c.isActive, c.isLocked, c.isAdmin, c.tenantIds, c.roleUuids]
        .filter((v) => Array.isArray(v) && v.length > 0).length
    },
    /**
     * The brand selected in the HEADER, if any.
     *
     * This list is scoped by it, server-side, and that is the one narrowing
     * with no control on this screen to show it — the filter panel's badge
     * counts the five filters here and knows nothing about the header. The
     * result is "I just created that account and it is not in the list", which
     * is what this exists to answer.
     */
    headerBrandName() {
      const id = tenantHelper.current()
      if (id == null) return ''

      const found = this.tenantRows.find((t) => String(t.tenant_id) === String(id))
      return found ? found.name : i18n.t('label.brandN', [id])
    },
  },
  async created() {
    this.initSearchCriteria()

    // Both are filter vocabularies, and neither should keep the table from
    // rendering — hence no await on the listing above them.
    this.loadTenants()
    this.getAllRole({ filter: { pageNumber: 1, pageSize: 100 } })
  },
  methods: {
    ...usersMethods,
    ...roleMethods,

    initSearchCriteria() {
      if (sessionHelper.hasSearchCriteria(SESSION.USERS)) {
        this.searchCriteria = sessionHelper.retrieveSearch(SESSION.USERS)
        this.filter = (this.searchCriteria.searchArray || [])[0] || ''
        this.showFilters = this.activeFilterCount > 0
      } else {
        this.searchCriteria = this.defaultCriteria()
      }

      this.getList(this.searchCriteria)
    },

    defaultCriteria() {
      return {
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        pageSize: SHARED.DEFAULT_PAGE_SIZE,
        searchArray: [],
        isActive: [],
        isLocked: [],
        isAdmin: [],
        tenantIds: [],
        roleUuids: [],
      }
    },

    getList(filter) {
      this.getAllUser({ filter })
      this.searchCriteria = filter
      // Stashed so a trip to the form and back shows the page you left, not
      // page one of an unfiltered list.
      sessionHelper.storeSearch(SESSION.USERS, filter)
    },

    pageNavigation(pageNumber) {
      this.getList({ ...this.searchCriteria, pageNumber })
    },

    searchListing() {
      this.getList({
        ...this.searchCriteria,
        // Back to page one: staying on page 4 of a freshly narrowed list is an
        // empty table that reads as "no results".
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        searchArray: this.filter ? [this.filter] : [],
      })
    },

    resetFilters() {
      this.filter = ''
      this.getList(this.defaultCriteria())
    },

    async loadTenants() {
      try {
        const result = await tenantService.list()
        this.tenantRows = result.data || []
        this.tenantDdl = ddlHelper.getTenantOptions(this.tenantRows, true)
      } catch (e) {
        // A missing brand filter is a narrower screen, not a broken one.
        this.tenantDdl = []
      }
    },

    /**
     * The brand cell, which can now hold several.
     *
     * Names where the server gave them, ids where it did not (a brand that has
     * not been synced into the mirror yet), joined with a comma. Not truncated:
     * an agent covering three brands is exactly the case this column exists to
     * make visible, and hiding the third behind "+1 more" defeats it.
     */
    tenantLabel(row) {
      if (row.isPlatformStaff) return i18n.t('label.platformAllBrands')

      const names = row.tenantNames && row.tenantNames.length
        ? row.tenantNames
        : (row.tenantIds || []).map((id) => i18n.t('label.brandN', [id]))

      return names.join(', ')
    },

    toggleStatus(row) {
      this.updateUsersStatus({
        id: row.id,
        isActive: !row.isActive,
        filter: this.searchCriteria,
      })
    },

    toggleLoginAccess(row) {
      this.updateUsersLoginAccess({
        id: row.id,
        isAllowLogin: !row.isAllowLogin,
        filter: this.searchCriteria,
      })
    },

    unlock(row) {
      this.unlockUsers({ id: row.id, filter: this.searchCriteria })
    },

    goCreate() {
      this.$router.push({ name: 'User-create' })
    },

    goEdit(row) {
      this.$router.push({ name: 'User-update', params: { id: row.id } })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center w-100">
                  {{ $t('label.search') }}
                  <b-form-input
                    v-model="filter"
                    type="search"
                    :placeholder="$t('label.search')"
                    class="form-control form-control-sm ml-2"
                    @change="searchListing"
                  ></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showFilters = !showFilters">
                  <i class="mdi mdi-filter-variant mr-1"></i>{{ $t('label.filter') }}
                  <b-badge v-if="activeFilterCount" variant="primary" class="ml-1">{{ activeFilterCount }}</b-badge>
                </b-button>
                <b-button v-if="$can('Create User')" variant="primary" size="sm" @click="goCreate">
                  <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.user')]) }}
                </b-button>
              </div>
            </div>

            <!-- Only when the header is narrowing the list. Said here rather
                 than left to the operator to connect, because the control that
                 causes it is on the other side of the screen. -->
            <div v-if="headerBrandName" class="small text-muted mb-3">
              <i class="mdi mdi-filter-outline mr-1"></i>
              {{ $t('user.scopedToBrand', [headerBrandName]) }}
            </div>

            <!-- Collapsed by default: five dropdowns above a table is a wall,
                 and most visits are "find this person". -->
            <b-collapse v-model="showFilters">
              <div class="border rounded p-3 mb-3 bg-light">
                <b-row>
                  <b-col cols="12" md="4" lg="3" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('label.status') }}</label>
                    <MultiSelect v-model="searchCriteria.isActive" :options="statusDdl" @change="searchListing" />
                  </b-col>
                  <b-col cols="12" md="4" lg="3" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('label.accountStatus') }}</label>
                    <MultiSelect v-model="searchCriteria.isLocked" :options="lockDdl" @change="searchListing" />
                  </b-col>
                  <b-col cols="12" md="4" lg="3" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('label.brand') }}</label>
                    <MultiSelect v-model="searchCriteria.tenantIds" :options="tenantDdl" @change="searchListing" />
                  </b-col>
                  <b-col cols="12" md="4" lg="3" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('label.role') }}</label>
                    <MultiSelect v-model="searchCriteria.roleUuids" :options="roleDdl" @change="searchListing" />
                  </b-col>
                  <b-col cols="12" md="4" lg="3" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('user.managesTeam') }}</label>
                    <MultiSelect v-model="searchCriteria.isAdmin" :options="adminDdl" @change="searchListing" />
                  </b-col>
                  <b-col cols="12" class="text-right">
                    <b-button variant="link" size="sm" @click="resetFilters">
                      {{ $t('label.clearFilters') }}
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </b-collapse>

            <div class="table-responsive mb-0">
              <b-table :items="tableRows" :fields="fields" responsive="sm" :busy="!status.list" show-empty>
                <template #empty>
                  <p class="text-muted text-center my-3">{{ $t('label.noRecordFound') }}</p>
                </template>

                <template #cell(name)="data">
                  {{ data.item.name }}
                  <b-badge v-if="data.item.isAdmin" variant="info" class="ml-1">
                    {{ $t('user.managesTeam') }}
                  </b-badge>
                  <!-- The commonest reason somebody "cannot log in": they
                       never followed the link, so there is no password yet. -->
                  <b-badge v-if="!data.item.isVerified" variant="warning" class="ml-1">
                    {{ $t('user.invited') }}
                  </b-badge>
                </template>

                <template #cell(email)="data">
                  <span class="small">{{ data.item.email }}</span>
                </template>

                <template #cell(tenant)="data">
                  <span class="small">{{ tenantLabel(data.item) }}</span>
                </template>

                <template #cell(roles)="data">
                  <b-badge v-for="r in data.item.roles" :key="r.id" variant="light" class="mr-1 border">
                    {{ r.name }}
                  </b-badge>
                  <span v-if="!data.item.roles.length" class="text-muted small">—</span>
                </template>

                <template #cell(isActive)="data">
                  <b-badge :variant="data.item.isActive ? 'success' : 'secondary'">
                    {{ data.item.isActive ? $t('label.active') : $t('label.inactive') }}
                  </b-badge>
                </template>

                <template #cell(isAllowLogin)="data">
                  <b-form-checkbox
                    switch
                    :disabled="!$can('Update User')"
                    :checked="data.item.isAllowLogin"
                    @change="toggleLoginAccess(data.item)"
                  >
                    <span :class="data.item.isAllowLogin ? 'text-success' : 'text-muted'">
                      {{ data.item.isAllowLogin ? $t('label.yes') : $t('label.no') }}
                    </span>
                  </b-form-checkbox>
                </template>

                <template #cell(isLocked)="data">
                  <b-badge :variant="data.item.isLocked ? 'danger' : 'success'">
                    {{ data.item.isLocked ? $t('label.lock') : $t('label.unlock') }}
                  </b-badge>
                </template>

                <template #cell(action)="data">
                  <a
                    v-if="$can('Update User')"
                    href="javascript:void(0);"
                    class="text-info mr-2"
                    v-b-tooltip.hover
                    :title="$t('label.updateX', [$t('module.user')])"
                    @click="goEdit(data.item)"
                  >
                    <i class="mdi mdi-pencil font-size-18"></i>
                  </a>
                  <a
                    v-if="$can('Update User')"
                    href="javascript:void(0);"
                    class="text-warning mr-2"
                    v-b-tooltip.hover
                    :title="$t('label.status')"
                    @click="toggleStatus(data.item)"
                  >
                    <i class="mdi mdi-power font-size-18"></i>
                  </a>
                  <a
                    v-if="data.item.isLocked && $can('Update User')"
                    href="javascript:void(0);"
                    class="text-danger"
                    v-b-tooltip.hover
                    :title="$t('label.unlock')"
                    @click="unlock(data.item)"
                  >
                    <i class="mdi mdi-lock-open-outline font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>

            <ListingPagination :pagination="pagination" @change="pageNavigation" />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
